import React, { FC, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, FormControl, TextInput } from "@skyportal/ui-kit";
import cn from "classnames";
import SwitchField from "components/SwitchField";
import { useTranslation } from "react-i18next";
import BucketModalShell from "../BucketModalShell";
import { OBJECT_LOCK_OPTIONS } from "../constants";
import shellStyles from "../BucketModalShell/BucketModalShell.module.css";
import { BaseBucketModalProps, SelectOption } from "../types";
import { getBucketNameValidationError } from "../validation";

import styles from "./CreateBucketModal.module.css";

interface Props extends BaseBucketModalProps {
  accountOptions: SelectOption[];
  locationOptions: SelectOption[];
  existingBucketNames: string[];
}

const CreateBucketModal: FC<Props> = ({ open, onClose, accountOptions, locationOptions, existingBucketNames }) => {
  const { t } = useTranslation("storageOverview");

  const [name, setName] = useState("");
  const [account, setAccount] = useState("");
  const [location, setLocation] = useState("");
  const [objectLock, setObjectLock] = useState("off");
  const [replicated, setReplicated] = useState(false);
  const [versioning, setVersioning] = useState(false);

  const isVersioningForced = objectLock !== "off" || replicated;

  const accountDropdownOptions = useMemo(
    () => accountOptions.map((opt) => ({ label: opt.label, value: opt.value })),
    [accountOptions]
  );

  const locationDropdownOptions = useMemo(
    () => locationOptions.map((opt) => ({ label: opt.label, value: opt.value })),
    [locationOptions]
  );
  const trimmedName = name.trim();
  const bucketNameError = useMemo(
    () => getBucketNameValidationError(name, existingBucketNames),
    [name, existingBucketNames]
  );
  const isSaveDisabled = !trimmedName || !!bucketNameError;

  useEffect(() => {
    if (!open) return;

    setName("");
    setAccount(accountOptions[0]?.value ?? "");
    setLocation(locationOptions[0]?.value ?? "");
    setObjectLock("off");
    setReplicated(false);
    setVersioning(false);
  }, [open, accountOptions, locationOptions]);

  const handleSave = () => {
    if (isSaveDisabled) {
      return;
    }

    const objectLockMs = objectLock === "off" ? null : Number(objectLock) * 24 * 60 * 60 * 1000;

    console.log("CREATE BUCKET", {
      name: trimmedName,
      account,
      location,
      objectLock: objectLockMs,
      replicated,
      versioning: isVersioningForced ? true : versioning,
    });

    onClose();
  };

  return (
    <BucketModalShell
      open={open}
      onClose={onClose}
      title={t("modals.create.title")}
      controls={
        <div className={cn(shellStyles.controls, styles.controls)}>
          <Button type="secondary" onClick={onClose}>
            {t("common:Cancel")}
          </Button>
          <Button type="primary" onClick={handleSave} disabled={isSaveDisabled}>
            {t("common:Save")}
          </Button>
        </div>
      }
    >
      <div className={cn(shellStyles.form, styles.form)}>
        <div className={cn(shellStyles.field, styles.field)}>
          <div className={styles.label}>{t("modals.fields.account")}</div>
          <Dropdown
            className={styles.dropdown}
            value={account || ""}
            options={accountDropdownOptions}
            onChange={setAccount}
            isOutlined
          />
        </div>

        <div className={cn(shellStyles.field, styles.field)}>
          <FormControl
            label={t("modals.fields.name")}
            error={bucketNameError ? t(bucketNameError) : undefined}
            fullWidth
          >
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </FormControl>
        </div>

        <div className={cn(shellStyles.field, styles.field)}>
          <div className={styles.label}>{t("modals.fields.location")}</div>
          <Dropdown
            className={styles.dropdown}
            value={location || ""}
            options={locationDropdownOptions}
            onChange={setLocation}
            isOutlined
          />
        </div>

        <div className={cn(shellStyles.field, styles.field)}>
          <div className={styles.label}>{t("modals.fields.objectLock")}</div>
          <Dropdown
            className={styles.dropdown}
            value={objectLock}
            options={OBJECT_LOCK_OPTIONS}
            onChange={setObjectLock}
            isOutlined
          />
        </div>

        <div className={cn(shellStyles.field, styles.field)}>
          <SwitchField label={t("table.columns.replicated")} checked={replicated} onChange={setReplicated} />
        </div>

        <div className={cn(shellStyles.field, styles.field)}>
          <SwitchField
            label={t("modals.fields.versioning")}
            checked={isVersioningForced ? true : versioning}
            disabled={isVersioningForced}
            onChange={setVersioning}
          />
        </div>
      </div>
    </BucketModalShell>
  );
};

export default CreateBucketModal;
